import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useInRouterContext } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Button, KpiCard, PageHeader, PagedFooter, PillTabs, Track, Spinner, ErrorPanel } from '../../components/ui';
import { LINK_BTN } from '../../components/ui/linkButton';
import { useAlertRulesList, useAlertsInbox } from '../../api/hooks';
import { SchoolApi } from '../../api/client';
import { CATEGORY_LABEL, CATEGORY_ORDER, categoryOf, compactSectionName, type RuleCategory } from '../../lib/alertRuleMeta';
import { nameOr } from '../../lib/names';
import usePagination from '../../lib/usePagination';
import AlertRuleCard, { ruleDraftDirty, type RuleDraft } from './components/AlertRuleCard';
import DesignatedCounselorCard from './components/DesignatedCounselorCard';

type Filter = 'all' | RuleCategory;

const GHOST_LINK = LINK_BTN;

function SectionOverridesLink() {
  const inRouter = useInRouterContext();
  return inRouter
    ? <Link className={GHOST_LINK} to="/settings/sections">Section overrides</Link>
    : <a className={GHOST_LINK} href="/settings/sections">Section overrides</a>;
}

export default function AlertSettingsPage() {
  const { data, isLoading, isError, refetch } = useAlertRulesList();
  const inbox = useAlertsInbox();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>('all');
  const [drafts, setDrafts] = useState<Record<string, RuleDraft>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const rules = useMemo(() => data?.rules ?? [], [data]);
  const shown = useMemo(() => (filter === 'all' ? rules : rules.filter((r) => categoryOf(r.code) === filter)), [rules, filter]);
  const pg = usePagination(shown, 10, filter);
  const draftOf = (r: (typeof rules)[number]): RuleDraft => drafts[r.code] ?? { isEnabled: r.is_enabled, overrides: r.threshold_overrides };
  const dirtyRules = rules.filter((r) => drafts[r.code] && ruleDraftDirty(r, drafts[r.code]));
  const setDraft = (code: string, next: RuleDraft) => { setJustSaved(false); setDrafts((prev) => ({ ...prev, [code]: next })); };
  const discard = () => { setDrafts({}); setSaveError(null); setJustSaved(false); };
  const saveAll = async () => {
    setSaving(true);
    setSaveError(null);
    const failed: string[] = [];
    const saved: string[] = [];
    for (const r of dirtyRules) {
      const d = drafts[r.code];
      try {
        await SchoolApi.alertRuleOverride(r.code, { is_enabled: d.isEnabled, threshold_overrides: d.overrides });
        saved.push(r.code);
      } catch {
        failed.push(r.name);
      }
    }
    // Keep saved drafts until the refetch lands so the cards never flash stale server values.
    try { await qc.invalidateQueries({ queryKey: ['school', 'alert-rules'] }); } catch { /* the list shows its own error state */ }
    if (saved.length) setDrafts((prev) => { const n = { ...prev }; saved.forEach((c) => { delete n[c]; }); return n; });
    if (failed.length) setSaveError(`Could not save: ${failed.join(', ')}`);
    setJustSaved(saved.length > 0 && failed.length === 0);
    setSaving(false);
  };
  // Preferred: the backend's 7-day totals (every alert fired, any status). Older backends only expose the
  // open inbox (no resolved or dismissed alerts, capped at 200), so that fallback is labelled "Open alerts".
  const weekAgo = Date.now() - 7 * 24 * 3600 * 1000;
  const recent = (inbox.data?.results ?? []).filter((a) => new Date(a.fired_at).getTime() >= weekAgo);
  const stats7 = data?.stats_7d
    ?? (inbox.data ? { total: recent.length, critical: recent.filter((a) => a.severity === 'critical').length, open: recent.filter((a) => a.status === 'open').length } : null);
  const overrides = data?.section_overrides ?? null;
  // Who saved last, from the refetched rows (real data) for the "All changes saved" bar.
  const lastSaver = [...(data?.rules ?? [])].filter((r) => r.updated_at).sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at)))[0];

  if (isLoading) return <div className="flex justify-center py-16"><Spinner size="md" /></div>;
  if (isError || !data) return <ErrorPanel onRetry={() => refetch()} />;

  const nOn = rules.filter((r) => draftOf(r).isEnabled).length;
  const pct = rules.length > 0 ? Math.round((nOn * 100) / rules.length) : 0;
  const countOf = (c: RuleCategory) => rules.filter((r) => categoryOf(r.code) === c).length;
  const options: { key: Filter; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: rules.length },
    ...CATEGORY_ORDER.filter((c) => c !== 'other' || countOf('other') > 0)
      .map((c) => ({ key: c as Filter, label: CATEGORY_LABEL[c], count: countOf(c) })),
  ];

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="Alert rules"
        subtitle={`${nOn} of ${rules.length} rules on. Tune thresholds for your school; changes apply to the next nightly evaluator run.`}
        actions={
          <>
            <SectionOverridesLink />
            <Button variant="ghost" onClick={discard} disabled={dirtyRules.length === 0 || saving}>Reset to saved</Button>
          </>
        }
      />

      <div className="mb-[18px] grid grid-cols-1 gap-[18px] min-[821px]:grid-cols-2 min-[1181px]:grid-cols-[5fr_7fr]">
        <DesignatedCounselorCard />
        <div className="grid grid-cols-1 gap-[18px] self-start sm:grid-cols-3">
          <KpiCard
            label="Rules on"
            size="lg"
            value={nOn}
            unit={<span className="text-muted"> / {rules.length}</span>}
            track={<Track value={pct} tone="slate" label="Share of rules switched on" />}
          />
          {stats7 && (
            <KpiCard
              label={data?.stats_7d ? 'Alerts · 7 days' : 'Open alerts · 7 days'}
              size="lg"
              value={stats7.total}
              sub={`${stats7.critical} critical · ${stats7.open} still open`}
            />
          )}
          {overrides && (
            <KpiCard
              label="Section overrides"
              size="lg"
              value={overrides.count}
              sub={overrides.sections.length > 0 ? overrides.sections.map(compactSectionName).join(' and ') : 'None yet'}
            />
          )}
        </div>
      </div>

      <div className="mb-4 mt-1.5 flex flex-wrap items-center justify-between gap-3">
        <PillTabs label="Rule category" scroll options={options} value={filter} onChange={setFilter} />
        <span className="text-[13px] text-muted">Leave a threshold empty to use the default.</span>
      </div>

      <div className="grid grid-cols-1 gap-[18px] min-[1181px]:grid-cols-2">
        {pg.pageItems.map((r) => (
          <AlertRuleCard key={r.code} rule={r} draft={draftOf(r)} onDraftChange={setDraft} />
        ))}
      </div>
      <PagedFooter from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} className="mt-4" />

      {dirtyRules.length > 0 && (
        <div role="region" aria-label="Unsaved changes" className="sticky bottom-4 z-10 mt-[22px] flex flex-wrap items-center gap-3.5 rounded-[18px] bg-navy py-3.5 pl-5 pr-4 text-white shadow-actbar">
          <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-accent" />
          <span className="min-w-0 flex-1 basis-52">
            <span className="block font-semibold">{dirtyRules.length} unsaved {dirtyRules.length === 1 ? 'change' : 'changes'}</span>
            <span className="text-xs text-on-navy-muted">Saved changes apply at the next nightly run.</span>
            {saveError && <span role="alert" className="mt-1 block text-xs text-alert-pill">{saveError}</span>}
          </span>
          <Button variant="ghost-on-navy" className="!border-navy-3" onClick={discard} disabled={saving}>Discard</Button>
          <Button onClick={saveAll} loading={saving}>Save changes</Button>
        </div>
      )}
      {justSaved && dirtyRules.length === 0 && (
        <div role="status" className="mt-[22px] flex flex-wrap items-center gap-3.5 rounded-[18px] bg-success-bg px-5 py-3.5 text-success">
          <Check size={18} strokeWidth={1.8} aria-hidden />
          <span className="font-semibold">
            All changes saved · {lastSaver ? `${nameOr(lastSaver.updated_by_name, lastSaver.updated_by_username)}, ` : ''}just now
          </span>
        </div>
      )}
    </>
  );
}
